"use client";

import { useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  Upload,
  Trash2,
  Download,
  Eye,
  X,
  ImageIcon,
} from "lucide-react";
import Card from "../components/Card";
import Button from "../components/Button";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";

// Generate mock images data
const generateMockImages = () => {
  const types = ["jpg", "png", "gif", "svg", "webp"];
  const categories = [
    "Products",
    "Banners",
    "Blog",
    "Users",
    "Backgrounds",
    "Icons",
  ];
  const dimensions = [
    "800x600",
    "1200x800",
    "400x400",
    "1920x1080",
    "300x200",
    "600x400",
    "1080x1080",
    "2560x1440",
    "640x480",
    "1024x768",
  ];

  return Array(60)
    .fill()
    .map((_, i) => {
      const type = types[i % types.length];
      const width = dimensions[i % dimensions.length].split("x")[0];
      const height = dimensions[i % dimensions.length].split("x")[1];

      return {
        id: i + 1,
        filename: `image_${i + 1}.${type}`,
        url: `/placeholder.svg?height=${height}&width=${width}`,
        thumbnail: `/placeholder.svg?height=200&width=200`,
        type: type,
        size: Math.floor(Math.random() * 5000) + 100, // Size in KB
        dimensions: dimensions[i % dimensions.length],
        category: categories[i % categories.length],
        uploaded_by: `user${(i % 5) + 1}`,
        created_at: new Date(
          Date.now() - Math.floor(Math.random() * 10000000000)
        ).toISOString(),
        alt_text: `Sample image ${i + 1} description`,
      };
    });
};

const Images = () => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [filters, setFilters] = useState({
    type: "",
    category: "",
  });
  const [uploadFiles, setUploadFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Load mock data
  useEffect(() => {
    const timer = setTimeout(() => {
      const mockImages = generateMockImages();
      setImages(mockImages);
      setFilteredImages(mockImages);
      applyPagination(mockImages, 1);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Apply filters and search
  useEffect(() => {
    let result = [...images];

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (image) =>
          image.filename.toLowerCase().includes(search) ||
          image.alt_text.toLowerCase().includes(search) ||
          image.category.toLowerCase().includes(search)
      );
    }

    // Apply type filter
    if (filters.type) {
      result = result.filter((image) => image.type === filters.type);
    }

    // Apply category filter
    if (filters.category) {
      result = result.filter((image) => image.category === filters.category);
    }

    setFilteredImages(result);
    setTotalPages(Math.ceil(result.length / getPageSize()));
    applyPagination(result, 1);
  }, [images, searchTerm, filters, viewMode]);

  // Get page size based on view mode
  const getPageSize = () => {
    return viewMode === "grid" ? 20 : 10;
  };

  // Apply pagination
  const applyPagination = (data, page) => {
    const pageSize = getPageSize();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setDisplayedImages(data.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const goToPage = (page) => {
    applyPagination(filteredImages, page);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already applied via useEffect
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setShowFilters(false);
  };

  const handleViewImage = (image) => {
    setSelectedImage(image);
    setIsViewModalOpen(true);
  };

  const handleDeleteImage = (image) => {
    setSelectedImage(image);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    // Delete image from the mock data
    const updatedImages = images.filter((img) => img.id !== selectedImage.id);
    setImages(updatedImages);

    // Show toast message (simplified)
    console.log(`Image "${selectedImage.filename}" has been deleted`);

    // Close the modal
    setIsDeleteModalOpen(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(files);
  };

  const handleUpload = () => {
    if (uploadFiles.length === 0) return;

    // Simulate upload process
    const newImages = uploadFiles.map((file, index) => {
      const id = images.length + index + 1;
      const fileType = file.name.split(".").pop();

      return {
        id,
        filename: file.name,
        url: `/placeholder.svg?height=400&width=600&text=${encodeURIComponent(
          file.name
        )}`,
        thumbnail: `/placeholder.svg?height=200&width=200&text=${encodeURIComponent(
          file.name
        )}`,
        type: fileType,
        size: Math.floor(file.size / 1024), // Convert bytes to KB
        dimensions: "600x400", // Placeholder
        category: filters.category || "Uncategorized",
        uploaded_by: "current_user",
        created_at: new Date().toISOString(),
        alt_text: file.name,
      };
    });

    // Add new images to the existing ones
    setImages([...newImages, ...images]);

    // Reset upload state
    setUploadFiles([]);
    setIsUploadModalOpen(false);

    // Show toast message (simplified)
    console.log(`${newImages.length} images uploaded successfully`);
  };

  // Get unique categories and types for filter dropdowns
  const categories = [...new Set(images.map((image) => image.category))].sort();
  const types = [...new Set(images.map((image) => image.type))].sort();

  // Format file size
  const formatFileSize = (sizeInKB) => {
    if (sizeInKB < 1000) {
      return `${sizeInKB} KB`;
    } else {
      return `${(sizeInKB / 1024).toFixed(2)} MB`;
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Images</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage and organize your media library
        </p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between mb-4">
          <form onSubmit={handleSearch} className="flex mb-4 sm:mb-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search images..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" className="ml-2">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              icon={Filter}
              className="ml-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filter
            </Button>
          </form>
          <div className="flex space-x-2">
            <div className="flex border border-gray-300 rounded-md">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === "grid"
                    ? "bg-blue-50 text-blue-600"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => setViewMode("grid")}
              >
                Grid
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === "list"
                    ? "bg-blue-50 text-blue-600"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => setViewMode("list")}
              >
                List
              </button>
            </div>
            <Button icon={Upload} onClick={() => setIsUploadModalOpen(true)}>
              Upload
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Filters</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Type
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  onChange={(e) =>
                    handleFilterChange({ type: e.target.value || "" })
                  }
                  value={filters.type}
                >
                  <option value="">All Types</option>
                  {types.map((type) => (
                    <option key={type} value={type}>
                      {type.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  onChange={(e) =>
                    handleFilterChange({ category: e.target.value || "" })
                  }
                  value={filters.category}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="animate-pulse">
            {viewMode === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="bg-gray-200 rounded-lg h-40"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="flex p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="bg-gray-200 rounded-lg h-16 w-16"></div>
                    <div className="ml-4 flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : displayedImages.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No images found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filters.
            </p>
            <div className="mt-6">
              <Button icon={Upload} onClick={() => setIsUploadModalOpen(true)}>
                Upload Images
              </Button>
            </div>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {displayedImages.map((image) => (
              <div
                key={image.id}
                className="group relative border border-gray-200 rounded-lg overflow-hidden cursor-pointer"
                onClick={() => handleViewImage(image)}
              >
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  <img
                    src={image.thumbnail || "/placeholder.svg"}
                    alt={image.alt_text}
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    <button
                      className="p-1 bg-white rounded-full text-gray-700 hover:text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewImage(image);
                      }}
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      className="p-1 bg-white rounded-full text-gray-700 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image);
                      }}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="p-2 truncate text-sm">
                  <p className="font-medium text-gray-900 truncate">
                    {image.filename}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {formatFileSize(image.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {displayedImages.map((image) => (
              <div
                key={image.id}
                className="flex items-center py-4 px-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleViewImage(image)}
              >
                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                  <img
                    src={image.thumbnail || "/placeholder.svg"}
                    alt={image.alt_text}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="ml-4 flex-1">
                  <div className="font-medium text-gray-900">
                    {image.filename}
                  </div>
                  <div className="mt-1 flex text-sm text-gray-500">
                    <span>{image.dimensions}</span>
                    <span className="mx-1">•</span>
                    <span>{formatFileSize(image.size)}</span>
                    <span className="mx-1">•</span>
                    <span>
                      {new Date(image.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <Badge variant="blue">{image.type.toUpperCase()}</Badge>
                </div>
                <div className="ml-4">
                  <Badge variant="gray">{image.category}</Badge>
                </div>
                <div className="ml-4 flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Eye}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewImage(image);
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Download}
                    onClick={(e) => {
                      e.stopPropagation();
                      // In a real app, this would trigger a download
                      console.log(`Downloading ${image.filename}`);
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Trash2}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(image);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      </Card>

      {/* View Image Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={selectedImage?.filename || "View Image"}
        size="lg"
      >
        {selectedImage && (
          <div className="space-y-4">
            <div className="flex justify-center bg-gray-100 rounded-lg p-2">
              <img
                src={selectedImage.url || "/placeholder.svg"}
                alt={selectedImage.alt_text}
                className="max-h-96 object-contain"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  File Information
                </h4>
                <dl className="mt-2 space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Name:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {selectedImage.filename}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Type:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {selectedImage.type.toUpperCase()}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Size:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {formatFileSize(selectedImage.size)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Dimensions:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {selectedImage.dimensions}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Usage Information
                </h4>
                <dl className="mt-2 space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Category:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {selectedImage.category}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Uploaded by:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {selectedImage.uploaded_by}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Upload date:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {new Date(selectedImage.created_at).toLocaleDateString()}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alt Text
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={selectedImage.alt_text}
                readOnly
              />
            </div>
            <div className="flex justify-between pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                icon={Download}
                onClick={() =>
                  console.log(`Downloading ${selectedImage.filename}`)
                }
              >
                Download
              </Button>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsViewModalOpen(false)}
                >
                  Close
                </Button>
                <Button
                  variant="danger"
                  icon={Trash2}
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleDeleteImage(selectedImage);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Image Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Image"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="ml-3"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        {selectedImage && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete the image "
              {selectedImage.filename}"? This action cannot be undone.
            </p>
            <div className="flex justify-center">
              <div className="h-32 w-32 border border-gray-200 rounded-md overflow-hidden">
                <img
                  src={selectedImage.thumbnail || "/placeholder.svg"}
                  alt={selectedImage.alt_text}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload Images"
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsUploadModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="ml-3"
              onClick={handleUpload}
              disabled={uploadFiles.length === 0}
            >
              Upload {uploadFiles.length > 0 && `(${uploadFiles.length})`}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              multiple
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
            />
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-5 w-5" />
              Select Files
            </button>
            <p className="mt-2 text-sm text-gray-500">or drag and drop</p>
            <p className="mt-1 text-xs text-gray-500">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>

          {uploadFiles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Selected Files
              </h4>
              <ul className="divide-y divide-gray-200 max-h-60 overflow-auto">
                {uploadFiles.map((file, index) => (
                  <li
                    key={index}
                    className="py-2 flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <ImageIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 truncate max-w-xs">
                        {file.name}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={() => {
                        setUploadFiles(
                          uploadFiles.filter((_, i) => i !== index)
                        );
                      }}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              onChange={(e) => handleFilterChange({ category: e.target.value })}
              value={filters.category}
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
              <option value="Uncategorized">Uncategorized</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Images;
